import { tendersService } from '@/services/tenders.service';
import axios from 'axios';

const initialState = () => ({
  tender: null,
  // Tender of the latest getTender request; late responses for another tender are dropped.
  requestedTenderId: null,
  // Match information (keywords + highlights) resolves independently of the tender.
  matches: null,
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
    matchesPending: false,
  },
});

const state = initialState();

const applyMatches = (tender, matches) => {
  tender.matchedKeywords = (matches && matches.matchedKeywords) || [];

  const highlights = (matches && matches.highlights) || {};
  if (highlights.title && highlights.title !== '') {
    tender.title = highlights.title;
  }
  if (highlights.description && highlights.description !== '') {
    tender.description = highlights.description;
  }
};

const actions = {
  refreshTender({ commit }, { id }) {
    commit('REFRESH_TENDER', { id });

    return tendersService.getTenderInfos(id).then(
      (tender) => {
        commit('SUCCESS', tender);
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
          throw error;
        }
      },
    );
  },
  // Resolves once the tender is loaded; matches (the slowest call) are applied whenever they arrive.
  getTender({ commit, state }, { id, keywords = null, explore = false }) {
    commit('GET_TENDER', { id });

    const matchesRequest = explore
      ? tendersService.getTenderMatchesForExplore({ id, searchKeywords: keywords })
      : tendersService.getTenderMatches({ id, keywords });

    matchesRequest.then(
      (matches) => {
        commit('SUCCESS_MATCHES', { id, matches });
      },
      (error) => {
        if (!axios.isCancel(error)) {
          console.error('Error retrieving tender match information:', error);
          commit('ERROR_MATCHES', { id, error });
        }
      },
    );

    return tendersService.getTenderInfos(id).then(
      (tender) => {
        commit('SUCCESS', tender);
      },
      (error) => {
        // Cancelled or superseded requests resolve silently, like a cancel.
        if (axios.isCancel(error) || state.requestedTenderId !== id) return;
        commit('ERROR', error);
        throw error;
      },
    );
  },
  read({ commit }, { id }) {
    return tendersService.read(id).then((userTender) => {
      commit('tenders/SET_USER_TENDER', userTender, { root: true });
      return commit('SET_USER_TENDER', userTender);
    });
  },
  unread({ commit }, { id }) {
    return tendersService.unread(id).then((userTender) => {
      commit('tenders/SET_USER_TENDER', userTender, { root: true });
      return commit('SET_USER_TENDER', userTender);
    });
  },
  follow({ commit }, { id }) {
    return tendersService.follow(id).then((userTender) => {
      commit('tenders/SET_USER_TENDER', userTender, { root: true });
      return commit('SET_USER_TENDER', userTender);
    });
  },
  unfollow({ commit }, { id }) {
    return tendersService.unfollow(id).then((userTender) => {
      commit('tenders/SET_USER_TENDER', userTender, { root: true });
      return commit('SET_USER_TENDER', userTender);
    });
  },
  share({ commit }, { id }) {
    return tendersService.share(id).then((tenantTender) => {
      commit('tenders/SET_TENANT_TENDER', tenantTender, { root: true });
      return commit('SET_TENANT_TENDER', tenantTender);
    });
  },
  unshare({ commit }, { id }) {
    return tendersService.unshare(id).then((tenantTender) => {
      commit('tenders/SET_TENANT_TENDER', tenantTender, { root: true });
      return commit('SET_TENANT_TENDER', tenantTender);
    });
  },
  archive({ commit }, { id }) {
    return tendersService.archive(id).then((userTender) => {
      commit('tenders/SET_USER_TENDER', userTender, { root: true });
      return commit('SET_USER_TENDER', userTender);
    });
  },
  unarchive({ commit }, { id }) {
    return tendersService.unarchive(id).then((userTender) => {
      commit('tenders/SET_USER_TENDER', userTender, { root: true });
      return commit('SET_USER_TENDER', userTender);
    });
  },
  rateUp({ commit }, { id }) {
    return tendersService
      .rate({ tenderId: id, thumbsRating: 'UP' })
      .then((tenantTender) => {
        commit('tenders/SET_TENANT_TENDER', tenantTender, { root: true });
        return commit('SET_TENANT_TENDER', tenantTender);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        commit('ERROR', error.response);
        throw error;
      });
  },
  rateDown({ commit }, { id }) {
    return tendersService
      .rate({ tenderId: id, thumbsRating: 'DOWN' })
      .then((tenantTender) => {
        commit('tenders/SET_TENANT_TENDER', tenantTender, { root: true });
        return commit('SET_TENANT_TENDER', tenantTender);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        commit('ERROR', error.response);
        throw error;
      });
  },
  unRate({ commit }, { id }) {
    return tendersService
      .unRate({ tenderId: id })
      .then((tenantTender) => {
        commit('tenders/SET_TENANT_TENDER', tenantTender, { root: true });
        return commit('SET_TENANT_TENDER', tenantTender);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        commit('ERROR', error.response);
        throw error;
      });
  },
  assign({ commit, dispatch, rootGetters }, { tenderId, userIds }) {
    return tendersService.assign({ tenderId, userIds }).then(
      (response) => {
        commit('tenders/UPDATE_ASSIGNEES', { tenderId, assignees: response }, { root: true });
        commit('UPDATE_ASSIGNEES', { tenderId, assignees: response });
        const currentUser = rootGetters['auth/currentUser'];
        if (currentUser && userIds.includes(currentUser.id)) {
          dispatch('notifications/getEvents', null, { root: true });
        }
      },
      (error) => {
        if (axios.isCancel(error)) return;
        commit('ERROR', error);
        throw error;
      },
    );
  },
  resetTender({ commit, state }) {
    // Vue creates the next route's component before destroying the current one, so a request the
    // next page has just started must survive this teardown.
    if (state.status.pending || state.status.matchesPending) return;
    commit('RESET_TENDER');
  },
  RESET(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const mutations = {
  REFRESH_TENDER(state, { id } = {}) {
    state.status = {
      error: null,
      pending: false,
      resolved: false,
      rejected: false,
      matchesPending: false,
    };
    state.requestedTenderId = id;
  },
  GET_TENDER(state, { id } = {}) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
      matchesPending: true,
    };
    state.requestedTenderId = id;
    state.matches = null;
  },
  SUCCESS(state, tender) {
    // Stale response: for a tender the user already left, or arriving after a reset.
    if (!state.requestedTenderId || tender.id !== state.requestedTenderId) return;

    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    // Declared before the object becomes reactive so later assignments are tracked.
    if (!tender.matchedKeywords) tender.matchedKeywords = [];
    if (state.matches) applyMatches(tender, state.matches);
    state.tender = tender;
  },
  SUCCESS_MATCHES(state, { id, matches }) {
    // Stale response for a tender the user already left.
    if (id !== state.requestedTenderId) return;

    state.matches = matches;
    state.status = {
      ...state.status,
      matchesPending: false,
    };
    if (state.tender && state.tender.id === id) applyMatches(state.tender, matches);
  },
  ERROR_MATCHES(state, { id }) {
    if (id !== state.requestedTenderId) return;

    state.status = {
      ...state.status,
      matchesPending: false,
    };
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
    state.tender = null;
  },
  SET_USER_TENDER(state, userTender) {
    if (state.tender && state.tender.id === userTender.tenderId) {
      state.tender.userTender = userTender;
    }
  },
  SET_TENANT_TENDER(state, tenantTender) {
    if (state.tender && state.tender.id === tenantTender.tenderId) {
      state.tender.tenantTender = tenantTender;
    }
  },
  UPDATE_ASSIGNEES(state, { tenderId, assignees }) {
    if (state.tender && state.tender.id === tenderId) {
      state.tender.assignees = assignees;
    }
  },
  RESET_TENDER(state) {
    state.tender = null;
    state.matches = null;
    state.requestedTenderId = null;
  },
};

const getters = {
  tender: (state) => state.tender,
  requestedTenderId: (state) => state.requestedTenderId,

  // matches
  matchedKeywords: (state) => (state.tender && state.tender.matchedKeywords) || [],

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
  matchesPending: (state) => state.status.matchesPending,
};

export const tender = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
